import { PermissionEnum } from '@Application/Security/Roles';
import PageHeader from '@Guidelines/Layout/PageHeader';
import Filters from '@UI/Pages/Administration/Users/Filters';
import UserSidebar from '@UI/Pages/Administration/Users/Sidebars/UserSidebar';
import { openUserSidebar } from '@UI/Pages/Administration/Users/Signals/userSidebarSignal';
import UsersContent from '@UI/Pages/Administration/Users/UsersContent';
import UsersConfirmationDialogs from '@UI/Pages/Administration/Users/ConfirmationDialogs/UsersConfirmationDialogs';
import UsersNotifications from '@UI/Pages/Administration/Users/UsersNotifications';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import { UserPlus } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';
import PageContainer from '@Guidelines/Layout/PageContainer';

/**
 * Assembles the page: header, `Filters`, the data-owning `UsersContent`, `UserSidebar`, `UsersConfirmationDialogs`
 * and notifications, in that visual order. `Filters` and `UserSidebar` cannot fetch their own `roles`/
 * `organizations` — they are plain, prop-driven components — so this page supplies them from the very
 * same sources `UsersContent` itself reads for its own logic: `rolesStore.accountRoles` through a
 * plain `useSelector` (never a second `useStoreQuery` — `UsersContent` already owns that fetch, this
 * only reads the value it populates) and `useCurrentOrganization()`, a context hook reading data
 * `CurrentOrganizationProvider` already fetched once for the whole session.
 *
 * `usersStore.users` is read the same way, through `useSelector`, only for the header's own title/
 * description — this component fetches nothing of its own.
 */
const UsersPage: React.FC = () => {
	const canWrite = useHasPermission(PermissionEnum.USER_WRITE);
	const canReadAccountUsers = useHasPermission(PermissionEnum.USER_READ_ACCOUNT);
	const { currentFacility } = useCurrentFacility();
	const usersList = useSelector((state) => state.usersStore.users);
	const accountRoles = useSelector((state) => state.rolesStore.accountRoles);
	const { organizations, isResolved: areOrganizationsResolved } = useCurrentOrganization();

	const roles = React.useMemo<readonly RoleView[] | null>(() => accountRoles?.list ?? null, [accountRoles]);

	const handleInviteClick = React.useCallback(() => {
		openUserSidebar(null);
	}, []);

	const inviteButton = canWrite ? (
		<Button
			type="button"
			label="Inviter un utilisateur"
			icon={<UserPlus size={16} />}
			onClick={handleInviteClick}
		/>
	) : undefined;

	// Silence, on the facility route, would read as users having disappeared — the caller used to see
	// every facility's users at once and now sees one at a time (§5.9.6 of the SSO spec).
	const title = React.useMemo(() => {
		if (canReadAccountUsers || currentFacility === null) {
			return 'Utilisateurs';
		}

		return `Utilisateurs — ${currentFacility.name}`;
	}, [canReadAccountUsers, currentFacility]);

	const description = React.useMemo(() => {
		if (usersList === null) {
			return;
		}

		return `${usersList.totalRecords} compte${usersList.totalRecords > 1 ? 's' : ''}.`;
	}, [usersList]);

	return (
		<PageContainer>
			<PageHeader eyebrow="STRUCTURE" title={title} description={description} actions={inviteButton} />
			<Filters roles={roles} organizations={areOrganizationsResolved ? organizations : null} />
			<UsersContent />
			<UserSidebar
				roles={roles ?? []}
				assignableOrganizations={organizations}
				areOptionsLoading={roles === null || !areOrganizationsResolved}
			/>
			<UsersConfirmationDialogs />
			<UsersNotifications />
		</PageContainer>
	);
};

export default UsersPage;
